import { useState } from "react";
import type { FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da2j8ciamq5jpe1hrtvg";

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [formMsg, setFormMsg] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Honeypot check
    const hp = new FormData(form).get("company_alt");
    if (hp && String(hp).trim().length > 0) {
      setStatus("success");
      setFormMsg("已收到你的留言，我们会尽快与你联系！");
      form.reset();
      return;
    }

    setStatus("loading");
    setFormError("");
    setFormMsg("");

    const payload = new FormData(form);

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      const ok = res.ok && parsed?.code === "OK";
      const spamLike = serverMsg.toLowerCase().includes("spam");

      if (ok && !spamLike) {
        setStatus("success");
        setFormMsg(serverMsg || "已收到你的留言，我们会尽快与你联系！");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <section id="contact" className="w-full bg-background-100/70 py-14 md:py-20">
      <div className="mx-auto w-full max-w-5xl px-4">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          <div>
            <p className="text-sm font-medium text-primary-600">联系我们</p>
            <h2 className="mt-2 font-display text-3xl text-foreground-950 md:text-4xl">告诉我们你的想法</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              无论你想投稿推荐自己的同人作品、反馈分类建议，还是与我们一起共建这份同人本索引，
              都欢迎通过下面的表单留言。我们会认真阅读每一条消息。
            </p>

            <div className="mt-7 flex flex-col gap-3">
              <div className="flex items-center gap-3 text-sm text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-mail-line"></i>
                </span>
                <div>
                  <p className="font-medium text-foreground-950">邮箱</p>
                  <p className="text-foreground-600">hello@naruto-doujin.example.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className="ri-time-line"></i>
                </span>
                <div>
                  <p className="font-medium text-foreground-950">回复时间</p>
                  <p className="text-foreground-600">工作日 10:00 - 18:00</p>
                </div>
              </div>
            </div>
          </div>

          <form
            id="contactForm"
            data-readdy-form
            className="rounded-lg bg-background-50 p-6 ring-1 ring-background-200 md:p-8"
            onSubmit={handleSubmit}
            noValidate
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="name" className="text-sm font-medium text-foreground-900">
                  你的昵称
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="怎么称呼你"
                  className="rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm font-medium text-foreground-900">
                  邮箱
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收回复"
                  className="rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-1.5">
              <label htmlFor="topic" className="text-sm font-medium text-foreground-900">
                留言类型
              </label>
              <select
                id="topic"
                name="topic"
                className="rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                <option value="作品投稿">作品投稿</option>
                <option value="分类建议">分类建议</option>
                <option value="功能反馈">功能反馈</option>
                <option value="合作交流">合作交流</option>
              </select>
            </div>

            <div className="mt-4 flex flex-col gap-1.5">
              <label htmlFor="message" className="text-sm font-medium text-foreground-900">
                留言内容
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                maxLength={500}
                placeholder="请写下你的想法（最多500字）"
                className="resize-none rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              ></textarea>
            </div>

            {/* Honeypot */}
            <input
              type="text"
              name="company_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="hp-field"
            />

            <button
              type="submit"
              disabled={status === "loading"}
              className="mt-5 w-full whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "loading" ? "发送中…" : "提交留言"}
            </button>

            {status === "success" && (
              <p className="mt-3 flex items-center gap-1.5 text-sm text-primary-700">
                <i className="ri-checkbox-circle-line"></i>
                {formMsg || "已收到你的留言，我们会尽快与你联系！"}
              </p>
            )}
            {status === "error" && (
              <p className="mt-3 flex items-start gap-1.5 text-sm text-accent-600">
                <i className="ri-error-warning-line mt-0.5"></i>
                {formError}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}